import { FC, useState } from "react";

import { CleanupTypeEnum, TaskDriverStatusEnum } from "@/domain/models/common";
import { PhotoWithCommentType } from "@/domain/models/job";
import { useTranslator } from "@/i18n/useTranslator";

import {
  CameraPhotoWithCommentType,
  DriverCamera,
  DriverFlowBottomButton,
} from "../blocks";
import { DsText } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUploadServicePhotoMutation } from "../data/mutations/storageMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { checkTaskHasCleanupType } from "../utils/task";
import { DriverTopActionButton } from "./DriverTopActionButton";

const DriverAddPhotoAreasCleaned: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { t } = useTranslator();

  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });

  const {
    mutateAsync: uploadAreaCleanedPhoto,
    isPending: isUploadingAreaCleanedPhoto,
  } = useUploadServicePhotoMutation();

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const openCamera = () => setIsCameraOpen(true);
  const closeCamera = () => setIsCameraOpen(false);

  const uploadPhoto = async (photos: CameraPhotoWithCommentType[]) => {
    const cleanedAreaUploadedImages: PhotoWithCommentType[] = [];

    const uploadPromises = photos.map(
      async ({ base64PhotoString, comment }) => {
        const uploadedPhotoUrl = await uploadAreaCleanedPhoto({
          jobId,
          serviceId,
          base64PhotoString,
          baseName: "cleanedAreasPhotos",
        });

        cleanedAreaUploadedImages.push({ photoUrl: uploadedPhotoUrl, comment });
      },
    );

    await Promise.allSettled(uploadPromises);

    updateService({
      jobId,
      serviceId,
      cleanedAreasPhotos: cleanedAreaUploadedImages,
      driverFlowStatus: TaskDriverStatusEnum.TASK_REVIEW,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.TASK_REVIEW,
    });
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isUploadingAreaCleanedPhoto ||
    (isLoading &&
      variables?.driverFlowStatus === TaskDriverStatusEnum.TASK_REVIEW);

  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.FILLING_SQFT_CLEANED;

  const hasCleanupType = checkTaskHasCleanupType(taskData);

  const isFineSweep = taskData?.cleanupType === CleanupTypeEnum.FINESWEEP;
  const isRoughSweep = taskData?.cleanupType === CleanupTypeEnum.ROUGHSWEEP;
  const isFullDetail = taskData?.cleanupType === CleanupTypeEnum.FULLDETAIL;

  const shouldUploadThreePhotos = isFineSweep || isRoughSweep;

  const getPreviousStep = () => {
    // If the task doesn't have a cleanup type, go back to FILLING_INTERIOR_CLEANUP_TYPE
    if (!hasCleanupType) {
      return TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE;
    }

    if (taskData?.cleanupType === CleanupTypeEnum.FULLDETAIL) {
      // For "Full Detail" cleanup type, go back to FILLING_SQFT_CLEANED
      return TaskDriverStatusEnum.FILLING_SQFT_CLEANED;
    }

    // For "Rough Sweep" and "Fine Sweep", go back to FILLING_SQFT_PER_FLOOR
    return TaskDriverStatusEnum.FILLING_SQFT_PER_FLOOR;
  };

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: getPreviousStep(),
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: getPreviousStep(),
    });
  };

  const getStepNumber = () => {
    switch (true) {
      case isFineSweep || isRoughSweep:
        return 11;

      case isFullDetail:
        return 10;

      default:
        return 9;
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      {isCameraOpen ? (
        <DriverCamera
          mode="multiple-photos"
          onChoosePhoto={photos => {
            setIsCameraOpen(false);
            void uploadPhoto(photos);
          }}
          onCancelClick={closeCamera}
          minimumPhotos={shouldUploadThreePhotos ? 3 : 1}
        />
      ) : (
        <>
          <DriverTopActionButton
            jobId={jobId}
            serviceId={serviceId}
            taskId={taskId}
          />
          <div className="flex flex-grow w-full flex-col justify-center">
            <DsText variant="pageTitle">
              {t("driver_add_photo_areas_cleaned.title", {
                stepNumber: getStepNumber(),
              })}
            </DsText>
          </div>
          <DriverFlowBottomButton
            onNextButtonClick={openCamera}
            isNextButtonLoading={isNextButtonLoading}
            nextButtonTitle="take_photo"
            previousButtonTitle={
              hasCleanupType
                ? "driver_add_photo_areas_cleaned.sqft_cleaned_back_button"
                : "driver_add_photo_areas_cleaned.interior_cleaned_type_back_button"
            }
            isPreviousButtonLoading={isPreviousButtonLoading}
            onPreviousButtonClick={goToPreviousStep}
          />
        </>
      )}
    </div>
  );
};

export { DriverAddPhotoAreasCleaned };
